import { NavLink, Route, Routes } from "react-router-dom";

import { UserMenu } from "./components/UserMenu.tsx";
import { isSignedIn, useAuth } from "./lib/auth.ts";
import { BugCoverage } from "./pages/BugCoverage.tsx";
import { Chat } from "./pages/Chat.tsx";
import { Dashboard } from "./pages/Dashboard.tsx";
import { Graph } from "./pages/Graph.tsx";
import { Knowledge } from "./pages/Knowledge.tsx";
import { KnowledgeHub } from "./pages/KnowledgeHub.tsx";
import { KnowledgeLayout } from "./pages/KnowledgeLayout.tsx";
import { Login } from "./pages/Login.tsx";
import { Repositories } from "./pages/Repositories.tsx";
import { Settings } from "./pages/Settings.tsx";
import { Teams } from "./pages/Teams.tsx";
import { TokenUsage } from "./pages/TokenUsage.tsx";
import { Upload } from "./pages/Upload.tsx";

interface NavItem {
  path: string;
  label: string;
  end?: boolean;
  /** When true, only users with role="admin" see this entry. */
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { path: "/", label: "Dashboard", end: true },
  { path: "/upload", label: "Upload" },
  { path: "/repos", label: "Repositories" },
  { path: "/graph", label: "Service map" },
  { path: "/teams", label: "Teams" },
  { path: "/knowledge", label: "Knowledge" },
  { path: "/chat", label: "Ask" },
  { path: "/token-usage", label: "Token usage", adminOnly: true },
  { path: "/settings", label: "Settings", adminOnly: true },
];

export default function App() {
  const { me, isLoading } = useAuth();

  if (isLoading || !me) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-zinc-500">
        Loading…
      </div>
    );
  }

  if (!isSignedIn(me)) {
    return <Login me={me} />;
  }

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 antialiased">
      <nav className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-3">
          <img
            src="/oracus-logo.svg"
            alt="Oracus"
            width={93}
            height={24}
            className="h-6 w-auto"
          />
          <UserMenu user={me.user} session={me.session!} />
        </div>
        <div className="border-t border-zinc-200">
          <div className="mx-auto flex max-w-7xl flex-wrap gap-1 px-6 py-2">
          {NAV.filter((item) => !item.adminOnly || me.user.role === "admin").map(
            (item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-2.5 py-1 text-sm transition ${
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
          </div>
        </div>
      </nav>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/upload" element={<Upload />} />
        <Route path="/repos" element={<Repositories />} />
        <Route path="/graph" element={<Graph />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/knowledge" element={<KnowledgeLayout />}>
          <Route index element={<KnowledgeHub />} />
          <Route path="manage" element={<Knowledge />} />
        </Route>
        <Route path="/bugs" element={<BugCoverage />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/token-usage" element={<TokenUsage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Dashboard />} />
      </Routes>
    </div>
  );
}
